import { useCallback } from 'react'
import { NoteType, isPrivateNote, type Note } from '../utils/note'
import { usePrivateProjectNotes } from './useNotesFactory'
import useMoveNote, { type MoveNoteOption } from './useMoveNote'
import { type SidebarEntry } from '../modules/sidebar/SidebarBuilder'
import { trackEvent } from '../lib/analytics'

type MoveToTrashOptions = {
  recordName: string
  noteType: NoteType
  children?: SidebarEntry[]
  descendenceIds?: string[]
}

/**
 * Hook to move private project notes and folders to the @Trash folder
 * instead of permanently deleting them
 */
export function useMoveToTrash() {
  const { data: privateProjectNotes } = usePrivateProjectNotes()
  const moveNote = useMoveNote()

  const moveToTrash = useCallback(
    async ({
      recordName,
      noteType,
      children = [],
      descendenceIds = [],
    }: MoveToTrashOptions) => {
      // Only handle private notes - team notes should still use regular delete
      if (!isPrivateNote(noteType)) {
        throw new Error('useMoveToTrash only supports private notes')
      }

      // Get the note to check if it's a special folder that shouldn't be moved to trash
      const noteToMove = privateProjectNotes?.map.get(recordName)
      const specialFolders = ['@Templates', '@Archive', '@Trash']
      if (noteToMove && specialFolders.includes(noteToMove.filename)) {
        throw new Error(
          `Cannot move special folder "${noteToMove.filename}" to trash. Special folders like @Templates, @Archive, and @Trash cannot be deleted.`
        )
      }

      // Find the @Trash folder
      const trashFolders = privateProjectNotes?.map
        ? [...privateProjectNotes.map.values()].filter(
            (note) => note.isFolder && note.filename === '@Trash'
          )
        : []

      // Check if the note is already in trash (has @Trash as parent)
      const trashFolderId = trashFolders[0]?.recordName
      if (noteToMove?.parent === trashFolderId) {
        throw new Error('This item is already in the trash folder.')
      }

      if (trashFolders.length === 0) {
        throw new Error(
          'Trash folder not found. Please refresh the page and try again.'
        )
      }

      if (trashFolders.length > 1) {
        console.warn(
          `Found ${String(trashFolders.length)} @Trash folders. Using the first one.`
        )
      }

      const trashFolder = trashFolders[0]

      if (!trashFolder.recordName) {
        throw new Error('@Trash folder recordName is undefined')
      }

      // Prepare the move operation
      const moveOptions: MoveNoteOption = {
        recordName,
        noteType,
        parent: {
          recordName: trashFolder.recordName,
          noteType: NoteType.PROJECT_NOTE,
        },
        children,
        privateProjectNotes:
          privateProjectNotes?.map ?? new Map<string, Note>(),
        teamProjectNotes: new Map(), // Not needed for private notes
      }

      // Track the move to trash event
      trackEvent('WEB - Note Moved to Trash', {
        recordName,
        noteType,
        descendenceIds,
        trashFolderId: trashFolder.recordName,
      })

      // Execute the move
      return moveNote.mutateAsync(moveOptions)
    },
    [privateProjectNotes, moveNote]
  )

  return {
    moveToTrash,
    isLoading: moveNote.isLoading,
    error: moveNote.error,
    isSuccess: moveNote.isSuccess,
  }
}
